import { FC } from 'react';

import { useFormik } from 'formik';
import { toast } from 'sonner';
import useSWRMutation from 'swr/mutation';

import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';

import { BroadcastForm } from '../BroadcastForm/BroadcastForm';
import { BroadcastFormValues, validationSchema } from '../BroadcastForm/form';

import { mapBroadcastToForm, mapFormToUpdateDto } from './utils';

import { broadcastNotificationService } from 'v2/api/broadcastNotificationService';
import { BroadcastNotification } from 'v2/types/BroadcastNotificationModel';
import { handleHttpError } from 'v2/utils';

interface BroadcastFormDialogProps {
  isOpen: boolean;
  onClose: VoidFunction;
  notification: BroadcastNotification;
  onSuccess: VoidFunction;
}

export const BroadcastFormDialog: FC<BroadcastFormDialogProps> = ({ isOpen, onClose, notification, onSuccess }) => {
  const { trigger: update, isMutating } = useSWRMutation(
    ['updateBroadcastNotification', notification.id],
    broadcastNotificationService.update
  );

  const formik = useFormik<BroadcastFormValues>({
    initialValues: mapBroadcastToForm(notification),
    enableReinitialize: true,
    validationSchema,
    validateOnChange: true,
    validateOnBlur: true,
    onSubmit: async (values) => {
      try {
        await update(mapFormToUpdateDto(values));
        toast.success('Notification updated');
        onSuccess();
      } catch (error) {
        handleHttpError(error);
      }
    },
  });

  const handleClose = () => {
    onClose();
    formik.resetForm();
  };

  return (
    <Dialog open={isOpen} onClose={handleClose} scroll="paper" maxWidth="sm" fullWidth>
      <DialogTitle>Edit Notification</DialogTitle>

      <form id="broadcastEditForm" onSubmit={formik.handleSubmit}>
        <DialogContent className="form">
          <BroadcastForm formik={formik} />
        </DialogContent>

        <DialogActions>
          <Button onClick={handleClose} color="error" variant="outlined">
            Cancel
          </Button>

          <Button form="broadcastEditForm" type="submit" variant="contained" loading={isMutating}>
            Save
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};
